import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React from "react"
// @ts-ignore
import PrismCode from "react-prism"
import ReactTooltip from "react-tooltip"
import styled, { css } from "styled-components"

import { API, isRestAPI, Message, MessageKind } from "../reducer/common.types"
import {
  Button,
  device,
  HeaderText,
  ItalicText,
  Monospace,
  RegularText,
  SmallerMonospace
} from "../styles"
import { Card } from "./Card"

interface Props {
  /**
   * Invoked when the user clicks the "clear messages" button.
   */
  clearMessages: () => any

  /**
   * A list of all incoming, outgoing and decrypted messages
   */
  messages: Message[]

  /**
   * The currently selected API
   */
  api: API
}

const MessageContainer = styled(Card)`
  grid-area: messages;
  display: flex;
  overflow: auto;
  flex-direction: column;
`

const MessagesDiv = styled.div`
  overflow: auto;
  flex: 1;
`

const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
`
const StyledMessageContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media ${device.smallerThen.laptop} {
    flex-direction: column;
  }
`

const Code = styled(PrismCode)`
  flex: 1;
  margin-left: 1em !important;
`

const KindContainer = styled.div<{ kind: MessageKind }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${device.smallerThen.laptop} {
    align-items: start;
    flex-direction: row;
  }

  ${({ kind }) => {
    if (kind === "decrypted") {
      return css`
        background-color: green;
        border-radius: 0.5em;
      `
    } else {
      return css``
    }
  }}
`

const StyledToolTip = styled(ReactTooltip)`
  max-width: 50vw;
`

/**
 * Component that renders the received HTTP/WebSocket messages
 * to the user.
 */
export const IncomingMessages: React.FunctionComponent<Props> = ({
  clearMessages,
  messages,
  api
}) => {
  return (
    <MessageContainer>
      <HeaderDiv>
        <HeaderText>Messages</HeaderText>
        <Button onClick={clearMessages}>Clear messages</Button>
      </HeaderDiv>
      <MessagesDiv>
        {messages.length === 0 ? (
          <>
            <ItalicText>No messages received yet.</ItalicText>
            <RegularText>
              Connect to an API by using the dropdown menu, and then click
              "connect". All received messages from the API will appear here.
            </RegularText>
          </>
        ) : (
          messages.map(({ text, language, date, kind }) => {
            const directionText = kind.slice(0, 1).toUpperCase() + kind.slice(1)

            // we want to display a tooltip besides the message
            // label if:
            //    1) the message is decrypted (explain the decryption process)
            //    2) the message is incoming and from the historical API
            //      (mention that they need to pay with WebLN to decrypt)
            const labelCompanion =
              kind === "decrypted" ? (
                <FontAwesomeIcon
                  data-tip={true}
                  data-for="decrypted"
                  color="white"
                  icon={faQuestionCircle}
                />
              ) : kind === "received" &&
                isRestAPI(api) &&
                Object.getOwnPropertyNames(text).includes("encryptedData") ? (
                <SmallerMonospace>Pay with WebLN to decrypt</SmallerMonospace>
              ) : null

            return (
              <StyledMessageContainer key={date.toISOString()}>
                <div>
                  <KindContainer kind={kind}>
                    <Monospace style={{ textAlign: "left" }}>
                      {directionText}
                    </Monospace>
                    {labelCompanion}
                  </KindContainer>
                  <Monospace>{date.format("HH:mm:SSS")}</Monospace>
                </div>
                <Code component="pre" className={language}>
                  {typeof text === "string"
                    ? text
                    : JSON.stringify(text, null, 2)}
                </Code>
                <StyledToolTip id="decrypted" effect="solid">
                  When requesting data from our REST APIs, we send the data
                  before payment is received. The data is encrypted, with the
                  preimage to the invoice you receive as the decryption key. We
                  do this decryption automatically, locally in your browser.
                </StyledToolTip>
              </StyledMessageContainer>
            )
          })
        )}
      </MessagesDiv>
    </MessageContainer>
  )
}
